import { Copy, RotateCcw, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSavedSearches } from '../../hooks/useSavedSearches';
import type { FilterConfig } from '../../types/filters';
import {
  getSavedSearchSignature,
  sanitizeSavedSearchDefinition,
  type SavedSearch,
  type SavedSearchDefinition,
  type SavedSearchEntityType,
} from '../../types/saved-search';
import { Button, ConfirmDialog, Select, toast } from '../ui';
import { SaveSearchDialog } from './SaveSearchDialog';

interface SavedSearchToolbarProps {
  entityType: SavedSearchEntityType;
  currentDefinition: SavedSearchDefinition;
  defaultDefinition: SavedSearchDefinition;
  filterConfigs: FilterConfig[];
  visibleColumnCount: number;
  onApply: (definition: SavedSearchDefinition) => void;
}

export function SavedSearchToolbar({
  entityType,
  currentDefinition,
  defaultDefinition,
  filterConfigs,
  visibleColumnCount,
  onApply,
}: SavedSearchToolbarProps) {
  const { user } = useAuth();
  const { searches, loading, error, createSearch, updateSearch, deleteSearch } =
    useSavedSearches(entityType);
  const viewStorageKey = `crm.search-session-view.v1.${entityType}`;
  const [activeSearchId, setActiveSearchId] = useState(() => {
    try {
      return sessionStorage.getItem(viewStorageKey) ?? '';
    } catch {
      return '';
    }
  });
  const [saveMode, setSaveMode] = useState<'create' | 'update' | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Mémorise la vue sélectionnée pour la session (navigation aller-retour),
  // en complément de la mémorisation des critères (useSearchSessionMemory).
  useEffect(() => {
    try {
      if (activeSearchId) sessionStorage.setItem(viewStorageKey, activeSearchId);
      else sessionStorage.removeItem(viewStorageKey);
    } catch {
      // stockage indisponible : pas de mémorisation
    }
  }, [viewStorageKey, activeSearchId]);

  // Vue supprimée entre-temps (ou id périmé) : retour à « Toutes les lignes »
  useEffect(() => {
    if (loading || error || !activeSearchId) return;
    if (!searches.some((search) => search.id === activeSearchId)) setActiveSearchId('');
  }, [loading, error, searches, activeSearchId]);

  const activeSearch = searches.find((search) => search.id === activeSearchId);
  const isOwner = activeSearch?.owner_user_id === user?.id;
  const isDirty = activeSearch
    ? getSavedSearchSignature(activeSearch.definition) !==
      getSavedSearchSignature(currentDefinition)
    : getSavedSearchSignature(defaultDefinition) !== getSavedSearchSignature(currentDefinition);
  const ownSearches = searches.filter((search) => search.owner_user_id === user?.id);
  const sharedSearches = searches.filter((search) => search.owner_user_id !== user?.id);
  function applySearch(search: SavedSearch) {
    const sanitized = sanitizeSavedSearchDefinition(search.definition, filterConfigs);
    onApply(sanitized.definition);
    setActiveSearchId(search.id);
    if (sanitized.ignoredFilters.length > 0) {
      toast.warning(
        `Filtres ignorés car ils ne sont plus disponibles : ${sanitized.ignoredFilters.join(', ')}`
      );
    }
  }

  function handleSelection(id: string) {
    if (!id) {
      onApply(defaultDefinition);
      setActiveSearchId('');
      return;
    }
    const selected = searches.find((search) => search.id === id);
    if (selected) applySearch(selected);
  }

  async function handleSave(values: { name: string; isShared: boolean }) {
    if (saveMode === 'update' && activeSearch && isOwner) {
      const updated = await updateSearch(activeSearch.id, {
        name: values.name,
        isShared: values.isShared,
        definition: currentDefinition,
      });
      setActiveSearchId(updated.id);
      toast.success('Recherche mise à jour.');
      return;
    }

    const created = await createSearch({
      name: values.name,
      isShared: values.isShared,
      definition: currentDefinition,
    });
    setActiveSearchId(created.id);
    toast.success('Recherche enregistrée.');
  }

  async function handleDelete() {
    if (!activeSearch || !isOwner) return;
    try {
      await deleteSearch(activeSearch.id);
      onApply(defaultDefinition);
      setActiveSearchId('');
      toast.success('Recherche supprimée.');
    } catch {
      toast.error('Impossible de supprimer la recherche.');
    }
  }

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Select
          value={activeSearchId}
          onChange={(event) => handleSelection(event.target.value)}
          className="w-48"
          aria-label="Vue sauvegardée"
          disabled={loading || Boolean(error)}
        >
          <option value="">Toutes les lignes</option>
          {ownSearches.length > 0 && (
            <optgroup label="Mes recherches">
              {ownSearches.map((search) => (
                <option key={search.id} value={search.id}>
                  {search.name}
                </option>
              ))}
            </optgroup>
          )}
          {sharedSearches.length > 0 && (
            <optgroup label="Partagées">
              {sharedSearches.map((search) => (
                <option key={search.id} value={search.id}>
                  {search.name}
                </option>
              ))}
            </optgroup>
          )}
        </Select>

        {isDirty && <span className="text-xs text-amber-700">Modifiée</span>}

        <Button
          type="button"
          size="icon"
          variant="outline"
          onClick={() => setSaveMode(activeSearch && isOwner ? 'update' : 'create')}
          aria-label={
            activeSearch && isOwner ? 'Mettre à jour la recherche' : 'Enregistrer la recherche'
          }
        >
          <Save />
        </Button>

        {activeSearch && (
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => setSaveMode('create')}
            aria-label="Enregistrer sous"
          >
            <Copy />
          </Button>
        )}

        {activeSearch && isDirty && (
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => applySearch(activeSearch)}
            aria-label="Réinitialiser la recherche"
          >
            <RotateCcw />
          </Button>
        )}

        {activeSearch && isOwner && (
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => setDeleteOpen(true)}
            aria-label="Supprimer la recherche"
          >
            <Trash2 className="text-red-600" />
          </Button>
        )}
      </div>

      <SaveSearchDialog
        isOpen={saveMode !== null}
        onClose={() => setSaveMode(null)}
        onSubmit={handleSave}
        initialName={saveMode === 'update' ? activeSearch?.name : ''}
        initialIsShared={saveMode === 'update' ? activeSearch?.is_shared : false}
        isUpdate={saveMode === 'update'}
        filterCount={currentDefinition.filters.length}
        visibleColumnCount={visibleColumnCount}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Supprimer cette recherche ?"
        description="La recherche sauvegardée sera supprimée. Les données de la liste ne seront pas modifiées."
        confirmLabel="Supprimer"
        variant="destructive"
        onConfirm={() => void handleDelete()}
      />
    </>
  );
}
